import { useActiveProduct } from "@/core/context/ActiveProductContext"
import MemberGroupTag from "@/product/common/member-group/common/tag/MemberGroupTag"
import { MemberGroupTagFragment$key } from "@/product/common/member-group/common/tag/__generated__/MemberGroupTagFragment.graphql"
import { ProductMembersListPageGroupFilterQuery } from "@/product/member/page/filters/__generated__/ProductMembersListPageGroupFilterQuery.graphql"
import { GlobalID } from "@/relay/RelayTypes"
import Relay from "@/relay/relayUtils"
import { DiscoIcon, SelectOption } from "@disco-ui"
import DiscoDropdownItem from "@disco-ui/dropdown/DiscoDropdownItem"
import DiscoPaginatedDropdownContent, {
  DiscoPaginatedDropdownContentSkeleton,
} from "@disco-ui/dropdown/DiscoPaginatedDropdownContent"
import { ArrayUtils } from "@utils/array/arrayUtils"
import { TestIDProps } from "@utils/typeUtils"
import { graphql } from "react-relay"

const GROUPS_PER_LOAD = 10

type ProductMembersListGroupFilterOption = SelectOption<
  "all" | "unassigned" | GlobalID
> & {
  groupKey?: MemberGroupTagFragment$key | null
}

export type ProductMembersListPageGroupFilterState = {
  selectedGroup: ProductMembersListGroupFilterOption
}

interface ProductMembersListPageGroupFilterProps extends TestIDProps {
  filterState: ProductMembersListPageGroupFilterState
  setFilterState: (state: ProductMembersListPageGroupFilterState) => void
}

function ProductMembersListPageGroupFilter({
  filterState,
  setFilterState,
}: ProductMembersListPageGroupFilterProps) {
  const activeProduct = useActiveProduct()

  const { data, pagination } =
    Relay.useRefetchablePaginationQuery<ProductMembersListPageGroupFilterQuery>(
      graphql`
        query ProductMembersListPageGroupFilterQuery(
          $id: ID!
          $first: Int!
          $search: String
          $after: String
        ) {
          product: node(id: $id) {
            ... on Product {
              memberGroups(first: $first, after: $after, search: $search, kind: custom)
                @connection(key: "ProductMembersListPageGroupFilter__memberGroups") {
                edges {
                  node {
                    id
                    name
                    ...MemberGroupTagFragment
                  }
                }
                pageInfo {
                  startCursor
                  endCursor
                  hasNextPage
                  hasPreviousPage
                }
              }
            }
          }
        }
      `,
      {
        id: activeProduct?.id,
        first: GROUPS_PER_LOAD,
      },
      { connectionName: "product.memberGroups", fetchPolicy: "network-only" }
    )

  const product = data?.product
  if (!product) return null

  const customMemberGroups = Relay.connectionToArray(product.memberGroups)

  const permissions = activeProduct?.viewerPermissions
  const canManageGroups = permissions?.has("member_groups.manage")

  const memberGroupFilterOptions = generateMemberGroupFilterOptions()
  const memberGroupTags = generateMemberTags()

  return (
    <DiscoPaginatedDropdownContent
      testid={"ProductMembersListPageGroupFilter.group-select"}
      pagination={pagination}
    >
      {memberGroupFilterOptions.map((option) => {
        const isActive =
          filterState.selectedGroup?.value === option.value ||
          (!filterState.selectedGroup && !option.value)
        return (
          <DiscoDropdownItem
            key={option.value}
            testid={`ProductMembersListPageGroupFilter.group-select.item-${option.title}`}
            title={memberGroupTags.get(option.value)}
            onClick={() => handleSelectGroupFilter(option)}
            rightButton={isActive && <DiscoIcon icon={"check"} marginLeft={1} />}
          />
        )
      })}
    </DiscoPaginatedDropdownContent>
  )

  function generateMemberGroupFilterOptions() {
    const options: ProductMembersListGroupFilterOption[] = [
      {
        value: "all",
        title: "All Groups",
      },
      ...ArrayUtils.spreadIf(
        {
          value: "unassigned",
          title: "Unassigned",
        },
        canManageGroups
      ),
    ]

    if (customMemberGroups.length) {
      options.push(
        ...customMemberGroups.map((mg) => ({
          value: mg.id,
          title: mg.name,
          groupKey: mg,
        }))
      )
    }

    return options
  }

  function generateMemberTags() {
    return new Map(
      memberGroupFilterOptions.map((option) => [
        option.value,
        option.groupKey ? (
          <MemberGroupTag key={option.title} memberGroupKey={option.groupKey} />
        ) : (
          option.title
        ),
      ])
    )
  }

  function handleSelectGroupFilter(selectedGroup: ProductMembersListGroupFilterOption) {
    setFilterState({ selectedGroup })
  }
}

export const ProductMembersListPageGroupFilterSkeleton = () => {
  return <DiscoPaginatedDropdownContentSkeleton />
}

export default Relay.withSkeleton({
  component: ProductMembersListPageGroupFilter,
  skeleton: ProductMembersListPageGroupFilterSkeleton,
})
